'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useAuth } from '@/contexts/AuthContext';
import { entryService, userService } from '@/lib/supabaseService';
import { CLIMBS, type ClimbEntry, type Climb, type UserProfile } from '@/lib/climbData';
import Icon from '@/components/ui/AppIcon';

interface ClimbStat {
  climb: Climb;
  entry: ClimbEntry | null;
  rank: number | null;
  totalParticipants: number;
  leaderTime: number | null;
}

function formatSeconds(s: number): string {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  return `${m}:${String(sec).padStart(2, '0')}`;
}

function RankBadge({ rank }: { rank: number }) {
  if (rank === 1) return <span className="inline-flex items-center justify-center w-7 h-7 rounded-full rank-gold text-xs font-bold">1</span>;
  if (rank === 2) return <span className="inline-flex items-center justify-center w-7 h-7 rounded-full rank-silver text-xs font-bold">2</span>;
  if (rank === 3) return <span className="inline-flex items-center justify-center w-7 h-7 rounded-full rank-bronze text-xs font-bold">3</span>;
  return <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-muted text-muted-foreground text-xs font-bold">{rank}</span>;
}

function DifficultyBadge({ difficulty }: { difficulty: Climb['difficulty'] }) {
  const map = {
    easy: { label: 'Facile', cls: 'bg-green-500/15 text-green-400 border-green-500/30' },
    medium: { label: 'Media', cls: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30' },
    hard: { label: 'Difficile', cls: 'bg-orange-500/15 text-orange-400 border-orange-500/30' },
    extreme: { label: 'Estrema', cls: 'bg-red-500/15 text-red-400 border-red-500/30' },
  };
  const { label, cls } = map[difficulty] ?? map.hard;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold border ${cls}`}>
      {label}
    </span>
  );
}

function ProgressRing({ value, max, size = 80 }: { value: number; max: number; size?: number }) {
  const radius = (size - 10) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = max > 0 ? Math.min(value / max, 1) : 0;
  const strokeDashoffset = circumference * (1 - progress);
  return (
    <svg width={size} height={size} className="-rotate-90">
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="hsl(var(--muted))" strokeWidth={8} />
      <circle
        cx={size / 2} cy={size / 2} r={radius} fill="none"
        stroke="hsl(var(--primary))" strokeWidth={8}
        strokeDasharray={circumference}
        strokeDashoffset={strokeDashoffset}
        strokeLinecap="round"
        style={{ transition: 'stroke-dashoffset 0.6s ease' }}
      />
    </svg>
  );
}

export default function PersonalProfilePage() {
  const { user: authUser, loading: authLoading } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [allEntries, setAllEntries] = useState<ClimbEntry[]>([]);
  const [allUsers, setAllUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState<'all' | 'completed' | 'missing'>('all');

  useEffect(() => {
    if (authLoading) return;
    async function load() {
      try {
        const [entries, users] = await Promise.all([
          entryService.getAll(),
          userService.getAll(),
        ]);
        setAllEntries(entries);
        setAllUsers(users);
        if (authUser) {
          const found = users.find((u) => u.id === authUser.id) ?? null;
          setProfile(found);
        } else {
          const demo = users.find((u) => u.status === 'active' && u.role !== 'admin') ?? null;
          setProfile(demo);
        }
      } catch {
        setAllEntries([]);
        setAllUsers([]);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [authUser, authLoading]);

  if (authLoading || loading) {
    return (
      <div className="py-24 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="py-24 flex flex-col items-center justify-center gap-4 fade-in">
        <Icon name="UserCircleIcon" size={56} className="text-muted-foreground" />
        <h1 className="text-2xl font-bold text-foreground">Profilo non disponibile</h1>
        <p className="text-muted-foreground text-sm">Accedi per visualizzare il tuo profilo personale.</p>
        <Link
          href="/sign-up-login-screen"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors"
        >
          <Icon name="UserCircleIcon" size={15} />
          Accedi
        </Link>
      </div>
    );
  }

  const userId = profile.id;

  // Build per-climb stats
  const climbStats: ClimbStat[] = CLIMBS.map((climb) => {
    const allApproved = allEntries
      .filter((e) => e.climbId === climb.id && e.status === 'approved')
      .sort((a, b) => a.timeSeconds - b.timeSeconds);
    const userEntry = allApproved.find((e) => e.userId === userId) ?? null;
    const rank = userEntry ? allApproved.findIndex((e) => e.userId === userId) + 1 : null;
    const leaderTime = allApproved.length > 0 ? allApproved[0].timeSeconds : null;
    return { climb, entry: userEntry, rank, totalParticipants: allApproved.length, leaderTime };
  });

  const completedClimbs = climbStats.filter((s) => s.entry !== null);
  const missingClimbs = climbStats.filter((s) => s.entry === null);
  const podiumFinishes = climbStats.filter((s) => s.rank !== null && s.rank <= 3).length;
  const leaderCount = climbStats.filter((s) => s.rank === 1).length;
  const bestRank = completedClimbs.length > 0 ? Math.min(...completedClimbs.map((s) => s.rank!)) : null;
  const avgWpkg = completedClimbs.length > 0
    ? completedClimbs.reduce((sum, s) => sum + (s.entry?.wattPerKg ?? 0), 0) / completedClimbs.length
    : 0;
  const totalTimeSeconds = completedClimbs.reduce((sum, s) => sum + (s.entry?.timeSeconds ?? 0), 0);

  // Overall rank calculation
  const activeUsers = allUsers.filter((u) => u.status === 'active');
  const userScores = activeUsers.map((u) => {
    const completed = CLIMBS.filter((c) => {
      const approved = allEntries.filter((e) => e.climbId === c.id && e.status === 'approved');
      return approved.some((e) => e.userId === u.id);
    }).length;
    const podiums = CLIMBS.filter((c) => {
      const sorted = allEntries
        .filter((e) => e.climbId === c.id && e.status === 'approved')
        .sort((a, b) => a.timeSeconds - b.timeSeconds);
      const idx = sorted.findIndex((e) => e.userId === u.id);
      return idx >= 0 && idx < 3;
    }).length;
    return { userId: u.id, score: completed * 10 + podiums * 5 };
  });
  userScores.sort((a, b) => b.score - a.score);
  const overallRank = userScores.findIndex((s) => s.userId === userId) + 1;

  // Rank history trend (simulated from entry dates)
  const sortedEntries = completedClimbs
    .filter((s) => s.entry !== null)
    .sort((a, b) => new Date(a.entry!.date).getTime() - new Date(b.entry!.date).getTime());

  const filteredStats = activeFilter === 'completed'
    ? climbStats.filter((s) => s.entry !== null)
    : activeFilter === 'missing'
    ? climbStats.filter((s) => s.entry === null)
    : climbStats;

  return (
    <div className="py-8 space-y-6 fade-in">

      {/* Profile Header */}
      <div className="bg-card border border-border rounded-2xl p-6 card-glow">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-6">
          <div className="flex items-center gap-5">
            <div className="relative flex-shrink-0">
              <div className="w-20 h-20 rounded-2xl bg-primary/20 flex items-center justify-center text-3xl font-bold text-primary border border-primary/30">
                {profile.avatar}
              </div>
              {profile.clubVerified && (
                <div className="absolute -bottom-1.5 -right-1.5 w-6 h-6 rounded-full bg-green-500 flex items-center justify-center border-2 border-card">
                  <Icon name="CheckBadgeIcon" size={12} className="text-white" />
                </div>
              )}
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <h1 className="text-2xl font-bold text-foreground">{profile.name}</h1>
                {profile.role === 'admin' && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-primary/15 text-primary border border-primary/30 text-xs font-semibold">
                    Admin
                  </span>
                )}
              </div>
              <p className="text-sm text-muted-foreground mb-2">
                {profile.email}
              </p>
              <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Icon name="CalendarIcon" size={12} />
                  Membro dal {new Date(profile.joinedAt).toLocaleDateString('it-IT', { year: 'numeric', month: 'long' })}
                </span>
                <span className="flex items-center gap-1">
                  {profile.authMethod === 'strava' ? (
                    <><Icon name="LinkIcon" size={12} /><span className="text-orange-400 font-semibold">Strava</span></>
                  ) : (
                    <><Icon name="EnvelopeIcon" size={12} />Email</>
                  )}
                </span>
                {profile.clubVerified && (
                  <span className="flex items-center gap-1 text-green-400">
                    <Icon name="CheckBadgeIcon" size={12} />
                    Membro IA Climb verificato
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Overall rank */}
          {overallRank > 0 && (
            <div className="flex flex-col items-center gap-1 flex-shrink-0">
              <div className="relative">
                <ProgressRing value={activeUsers.length - overallRank + 1} max={activeUsers.length} size={88} />
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className={`text-2xl font-bold font-mono-nums leading-none ${overallRank === 1 ? 'text-yellow-400' : overallRank <= 3 ? 'text-accent' : 'text-foreground'}`}>
                    #{overallRank}
                  </span>
                </div>
              </div>
              <span className="text-xs text-muted-foreground font-semibold">Rank globale</span>
            </div>
          )}
        </div>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Completion */}
        <div className="col-span-2 lg:col-span-1 bg-card border border-border rounded-2xl p-5 card-glow flex flex-col gap-1">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Salite completate</p>
          <div className="flex items-end gap-2 mt-1">
            <span className="text-4xl font-bold text-foreground font-mono-nums">{completedClimbs.length}</span>
            <span className="text-lg text-muted-foreground mb-1">/ {CLIMBS.length}</span>
          </div>
          <div className="mt-2 h-1.5 rounded-full bg-muted overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-primary to-accent transition-all"
              style={{ width: `${(completedClimbs.length / CLIMBS.length) * 100}%` }}
            />
          </div>
          <p className="text-xs text-muted-foreground mt-1">{Math.round((completedClimbs.length / CLIMBS.length) * 100)}% completato</p>
        </div>

        {/* Best rank */}
        <div className="bg-card border border-border rounded-2xl p-5 flex flex-col gap-1">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Miglior rank</p>
          <div className="flex items-center gap-2 mt-1">
            <Icon name="TrophyIcon" size={20} className="text-yellow-400" />
            <span className="text-4xl font-bold text-yellow-400 font-mono-nums">
              {bestRank !== null ? `#${bestRank}` : '—'}
            </span>
          </div>
          <p className="text-xs text-muted-foreground mt-auto">su tutte le salite</p>
        </div>

        {/* Podiums */}
        <div className="bg-card border border-border rounded-2xl p-5 flex flex-col gap-1">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Podi (top 3)</p>
          <div className="flex items-center gap-2 mt-1">
            <Icon name="StarIcon" size={20} className="text-accent" />
            <span className="text-4xl font-bold text-accent font-mono-nums">{podiumFinishes}</span>
          </div>
          <p className="text-xs text-muted-foreground mt-auto">{leaderCount} {leaderCount === 1 ? 'volta' : 'volte'} leader</p>
        </div>

        {/* Avg W/kg */}
        <div className="bg-card border border-border rounded-2xl p-5 flex flex-col gap-1">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Media W/kg</p>
          <div className="flex items-center gap-2 mt-1">
            <Icon name="BoltIcon" size={20} className="text-primary" />
            <span className="text-4xl font-bold text-foreground font-mono-nums">
              {avgWpkg > 0 ? avgWpkg.toFixed(1) : '—'}
            </span>
          </div>
          <p className="text-xs text-muted-foreground mt-auto">potenza media</p>
        </div>
      </div>

      {/* Rank Progress Chart */}
      {completedClimbs.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Rank bar chart */}
          <div className="bg-card border border-border rounded-2xl p-5">
            <h2 className="text-sm font-semibold text-foreground mb-5 flex items-center gap-2">
              <Icon name="ChartBarIcon" size={16} className="text-primary" />
              Rank per salita
            </h2>
            <div className="flex items-end gap-2 h-28">
              {completedClimbs.map((s, i) => {
                const maxRank = s.totalParticipants || 1;
                const barHeight = Math.max(10, Math.round(((maxRank - s.rank! + 1) / maxRank) * 100));
                const isTop = s.rank === 1;
                return (
                  <div key={`rank-bar-${i}`} className="flex-1 flex flex-col items-center gap-1.5">
                    <span className={`text-xs font-bold font-mono-nums ${isTop ? 'text-yellow-400' : 'text-muted-foreground'}`}>
                      #{s.rank}
                    </span>
                    <div
                      className="w-full rounded-t-md transition-all"
                      style={{
                        height: `${barHeight}%`,
                        background: isTop
                          ? 'linear-gradient(to top, #f59e0b, #fbbf24)'
                          : 'linear-gradient(to top, hsl(var(--primary)/0.5), hsl(var(--primary)/0.85))',
                      }}
                    />
                    <span className="text-[10px] text-muted-foreground truncate w-full text-center">
                      {s.climb.name.replace('Passo ', '').replace('Colle ', '').replace('Monte ', '')}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* PR Times summary */}
          <div className="bg-card border border-border rounded-2xl p-5">
            <h2 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="ClockIcon" size={16} className="text-primary" />
              Tempi PR per salita
            </h2>
            <div className="space-y-2.5">
              {completedClimbs.slice(0, 5).map((s) => {
                const gapPct = s.leaderTime && s.entry
                  ? Math.min(((s.entry.timeSeconds - s.leaderTime) / s.leaderTime) * 100, 50)
                  : 0;
                return (
                  <div key={`pr-${s.climb.id}`} className="flex items-center gap-3">
                    <div className="w-24 flex-shrink-0">
                      <p className="text-xs text-muted-foreground truncate">{s.climb.name.replace('Passo ', '').replace('Colle ', '').replace('Monte ', '')}</p>
                    </div>
                    <div className="flex-1 h-5 bg-muted rounded-full overflow-hidden relative">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{
                          width: `${100 - gapPct}%`,
                          background: s.rank === 1
                            ? 'linear-gradient(to right, #f59e0b, #fbbf24)'
                            : 'linear-gradient(to right, hsl(var(--primary)/0.6), hsl(var(--primary)))',
                        }}
                      />
                    </div>
                    <span className={`text-xs font-mono-nums font-bold w-16 text-right flex-shrink-0 ${s.rank === 1 ? 'text-yellow-400' : 'text-foreground'}`}>
                      {s.entry?.timeFormatted}
                    </span>
                    {s.rank !== null && <RankBadge rank={s.rank} />}
                  </div>
                );
              })}
              {completedClimbs.length > 5 && (
                <p className="text-xs text-muted-foreground text-center pt-1">+{completedClimbs.length - 5} altre salite</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Climb History Table */}
      <div className="bg-card border border-border rounded-2xl overflow-hidden card-glow">
        <div className="px-5 py-4 border-b border-border flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Icon name="ListBulletIcon" size={16} className="text-primary" />
            Storico salite — statistiche personali
          </h2>
          <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
            {(['all', 'completed', 'missing'] as const).map((f) => (
              <button
                key={f}
                onClick={() => setActiveFilter(f)}
                className={`px-3 py-1 rounded-md text-xs font-semibold transition-all ${
                  activeFilter === f
                    ? 'bg-card text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {f === 'all' ? `Tutte (${CLIMBS.length})` : f === 'completed' ? `Completate (${completedClimbs.length})` : `Mancanti (${missingClimbs.length})`}
              </button>
            ))}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40">
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground tracking-wider">Salita</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground tracking-wider">Difficoltà</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground tracking-wider">Rank</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground tracking-wider">Tempo PR</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground tracking-wider hidden sm:table-cell">Distacco</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground tracking-wider hidden md:table-cell">W/kg</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground tracking-wider hidden lg:table-cell">Partecipanti</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground tracking-wider hidden lg:table-cell">Data</th>
              </tr>
            </thead>
            <tbody>
              {filteredStats.map((s) => (
                <tr
                  key={`climb-row-${s.climb.id}`}
                  className={`border-b border-border/50 transition-all ${s.entry ? 'row-hover' : 'opacity-50'}`}
                >
                  <td className="px-4 py-3.5">
                    <div>
                      <p className="font-semibold text-foreground text-sm">{s.climb.name}</p>
                      <p className="text-xs text-muted-foreground">{s.climb.location} · {s.climb.distance} km · {s.climb.avgGradient}%</p>
                    </div>
                  </td>
                  <td className="px-4 py-3.5">
                    <DifficultyBadge difficulty={s.climb.difficulty} />
                  </td>
                  <td className="px-4 py-3.5">
                    {s.rank !== null ? (
                      <RankBadge rank={s.rank} />
                    ) : (
                      <span className="text-xs text-muted-foreground italic">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3.5">
                    {s.entry ? (
                      <span className={`font-mono-nums font-bold text-base ${s.rank === 1 ? 'text-accent' : 'text-foreground'}`}>
                        {s.entry.timeFormatted}
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground italic">Non completata</span>
                    )}
                  </td>
                  <td className="px-4 py-3.5 hidden sm:table-cell">
                    {s.entry ? (
                      s.entry.gapFormatted === 'Leader' ? (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-yellow-400">
                          <Icon name="TrophyIcon" size={12} />
                          Leader
                        </span>
                      ) : (
                        <span className="font-mono-nums text-sm text-muted-foreground">{s.entry.gapFormatted}</span>
                      )
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3.5 hidden md:table-cell">
                    <span className="font-mono-nums text-sm text-foreground">
                      {s.entry ? s.entry.wattPerKg.toFixed(1) : '—'}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 hidden lg:table-cell">
                    <span className="font-mono-nums text-sm text-muted-foreground">{s.totalParticipants}</span>
                  </td>
                  <td className="px-4 py-3.5 hidden lg:table-cell">
                    <span className="text-sm text-muted-foreground font-mono-nums">
                      {s.entry ? s.entry.date : '—'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-border flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-4">
            <span className="text-xs text-muted-foreground">
              Tempo totale:{' '}
              <span className="font-mono-nums font-bold text-foreground">
                {totalTimeSeconds > 0 ? formatSeconds(totalTimeSeconds) : '—'}
              </span>
            </span>
            <span className="text-xs text-muted-foreground">
              Media W/kg:{' '}
              <span className="font-mono-nums font-bold text-primary">
                {avgWpkg > 0 ? avgWpkg.toFixed(2) : '—'}
              </span>
            </span>
          </div>
          <Link
            href="/"
            className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="TrophyIcon" size={13} />
            Vedi classifica globale
          </Link>
        </div>
      </div>

      {/* Missing climbs nudge */}
      {missingClimbs.length > 0 && (
        <div className="bg-primary/5 border border-primary/20 rounded-2xl p-5 flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center flex-shrink-0">
            <Icon name="FlagIcon" size={18} className="text-primary" />
          </div>
          <div>
            <p className="text-sm font-semibold text-foreground mb-1">
              {missingClimbs.length} {missingClimbs.length === 1 ? 'salita ancora da completare' : 'salite ancora da completare'}
            </p>
            <p className="text-xs text-muted-foreground">
              {missingClimbs.map((s) => s.climb.name).join(', ')}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
